/**
 * PhotosFilesTab v2 — real multi-file upload backed by Emergent Object
 * Storage. Drag-and-drop or click to pick JPEG/PNG/WebP/GIF (≤10MB each,
 * ≤40 per property). First upload auto-seeds the property's hero_photo_url
 * so Pipeline cards & Command Center header light up.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ImageIcon, Upload, Trash2, ExternalLink, Star, StarOff,
  Plus, X,
} from "lucide-react";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/gif";
const MAX_MB = 10;

export default function PhotosFilesTab({ property, onPropertyChange }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/photos`);
      setPhotos(data.items || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [property.id]);
  useEffect(() => { load(); }, [load]);

  const publicBase = process.env.REACT_APP_BACKEND_URL || "";
  const absUrl = (u) => (u?.startsWith("http") ? u : `${publicBase}${u || ""}`);

  const uploadFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let uploaded = 0;
    let heroSet = false;
    for (const f of files) {
      if (!f.type?.startsWith("image/")) {
        toast.error(`${f.name}: not an image`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: over ${MAX_MB}MB`);
        continue;
      }
      const body = new FormData();
      body.append("file", f);
      try {
        const { data } = await api.post(
          `/v3/properties/${property.id}/photos`, body,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setPhotos((prev) => [
          { ...data, id: data.id, url: data.url, original_filename: f.name },
          ...prev,
        ]);
        uploaded += 1;
        if (!heroSet && !property.hero_photo_url) heroSet = true;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : `${f.name}: upload failed`);
      }
    }
    setUploading(false);
    if (uploaded > 0) toast.success(`Uploaded ${uploaded} photo${uploaded === 1 ? "" : "s"}`);
    // If backend just auto-seeded the hero, refresh property so the CC header updates.
    if (heroSet) {
      try {
        const { data } = await api.get(`/v3/properties/${property.id}`);
        onPropertyChange?.(data);
      } catch { /* noop */ }
    }
  }, [property.id, property.hero_photo_url, onPropertyChange]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    uploadFiles(files);
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/v3/properties/${property.id}/photos/${photo.id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      toast.success("Photo deleted");
      if (absUrl(property.hero_photo_url) === absUrl(photo.url)) {
        const { data } = await api.get(`/v3/properties/${property.id}`);
        onPropertyChange?.(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Delete failed");
    }
  };

  const setHero = async (photo) => {
    try {
      const { data } = await api.patch(`/v3/properties/${property.id}`, {
        hero_photo_url: photo.url,
      });
      onPropertyChange?.(data);
      toast.success("Hero photo updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't set hero");
    }
  };

  const clearHero = async () => {
    try {
      const { data } = await api.patch(`/v3/properties/${property.id}`, {
        hero_photo_url: null,
      });
      onPropertyChange?.(data);
      toast.success("Hero cleared");
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-4" data-testid="tab-panel-photos">
      {/* Drop-zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="photos-dropzone"
        className={
          "rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all " +
          (dragOver
            ? "border-amber-400 bg-amber-50/60"
            : "border-slate-300 bg-white hover:border-amber-300 hover:bg-amber-50/30")
        }
      >
        <input
          type="file" multiple accept={ACCEPT}
          ref={inputRef} onChange={onPick} className="hidden"
          data-testid="photos-file-input"
        />
        <Upload className="w-6 h-6 text-amber-500 mx-auto mb-2" />
        <div className="text-slate-900 font-bold text-sm">
          {uploading ? "Uploading…" : "Drop images here or click to choose"}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          JPEG · PNG · WebP · GIF · ≤{MAX_MB}MB each · up to 40 per property
        </div>
      </div>

      {/* Gallery */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-600 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading photos…
        </div>
      ) : photos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 py-8 text-center"
             data-testid="photos-empty">
          <ImageIcon className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-slate-900 font-semibold text-sm">No photos yet</div>
          <div className="text-[12px] text-slate-500 mt-1">
            Your first upload becomes the hero automatically.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
             data-testid="photos-gallery">
          {photos.map((p) => {
            const isHero = absUrl(property.hero_photo_url) === absUrl(p.url);
            return (
              <div
                key={p.id}
                data-testid={`photos-item-${p.id}`}
                className="group relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-square"
              >
                <img
                  src={absUrl(p.url)} alt={p.original_filename || "Property photo"}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                {isHero && (
                  <span
                    className="absolute top-1.5 left-1.5 rounded-md bg-amber-500 text-white text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-0.5"
                    title="Currently the hero photo"
                  >
                    <Star className="w-2.5 h-2.5" /> HERO
                  </span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  <a
                    href={absUrl(p.url)} target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-md bg-white text-slate-900 text-[10.5px] font-bold px-2 py-1 hover:bg-slate-100"
                  >
                    <ExternalLink className="w-3 h-3" /> View
                  </a>
                  {isHero ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); clearHero(); }}
                      data-testid={`photos-clear-hero-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-white text-slate-900 text-[10.5px] font-bold px-2 py-1 hover:bg-slate-100"
                    >
                      <StarOff className="w-3 h-3" /> Clear Hero
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setHero(p); }}
                      data-testid={`photos-set-hero-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-amber-500 text-white text-[10.5px] font-bold px-2 py-1 hover:bg-amber-600"
                    >
                      <Star className="w-3 h-3" /> Set Hero
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deletePhoto(p); }}
                    data-testid={`photos-delete-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-md bg-white text-rose-700 text-[10.5px] font-bold px-2 py-1 hover:bg-rose-50"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
          {/* + tile to fire the picker again */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="photos-add-more"
            disabled={uploading}
            className="rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-amber-300 hover:bg-amber-50/40 aspect-square flex flex-col items-center justify-center text-slate-500 hover:text-amber-700 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-6 h-6" />}
            <span className="text-[10.5px] font-bold mt-1">
              {uploading ? "Uploading" : "Add more"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
